import type { ConnectionEvent } from './post';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

export const connectionApi = {
  /** Global `linkedin_publish_mode` (share_api | community_api | manual). Drives
   *  whether the manual-stub connect tile + the LinkedIn XLS import show: once
   *  `community_api` (Community Mgmt API live), both are hidden. */
  linkedinPublishMode: (workspaceId: string) =>
    api
      .get<{
        linkedinPublishMode: string;
      }>(`/api/workspaces/${workspaceId}/settings`)
      .then((r) => r.data.linkedinPublishMode),
  // Workspace-level connections (legacy — house accounts)
  list: (workspaceId: string) =>
    api.get<PlatformConnection[]>(`/api/workspaces/${workspaceId}/connections`).then((r) => r.data),
  // All connections (multi-account — includes brand-level + workspace-level)
  listAll: (workspaceId: string) =>
    api
      .get<PlatformConnection[]>(`/api/workspaces/${workspaceId}/connections/all`)
      .then((r) => r.data),
  getAuthUrl: (
    workspaceId: string,
    platform: string,
    redirectUri: string,
    brandId?: string,
    /**
     * V135 — LinkedIn-only knob: `personal` triggers the Share-API OAuth
     * (openid + profile + w_member_social, separate dev app /
     * credentials), `company` (default) triggers the existing Community
     * Management OAuth. Other platforms ignore.
     */
    variant?: 'personal' | 'company',
  ) =>
    api
      .get<{
        url: string;
      }>(`/api/workspaces/${workspaceId}/connections/${platform}/authorize`, {
        params: { redirectUri, brandId, variant },
      })
      .then((r) => r.data),
  callback: (workspaceId: string, platform: string, code: string, redirectUri: string) =>
    api
      .post<PlatformConnection>(`/api/workspaces/${workspaceId}/connections/${platform}/callback`, {
        code,
        redirectUri,
      })
      .then((r) => r.data),
  /**
   * V136 — create a manual-only stub connection (no OAuth). Today only
   * LinkedIn company pages: the row exists so the brand → platform
   * binding shows up in the UI, every assigned post routes through the
   * MANUAL_PUBLISH card flow. When the real OAuth flow runs against the
   * same workspace the row upgrades in-place.
   */
  createManualConnection: (
    workspaceId: string,
    platform: string,
    body: { accountName: string; accountUrl?: string; accountType?: string },
  ) =>
    api
      .post<PlatformConnection>(
        `/api/workspaces/${workspaceId}/connections/${platform}/manual`,
        body,
      )
      .then((r) => r.data),
  // V137/V138/V139 — LinkedIn analytics XLS import. Returns the pending
  // ImportJob immediately; frontend polls `getAnalyticsImportJob`
  // until status terminates.
  //
  // The explicit multipart/form-data header is load-bearing: the
  // shared axios instance defaults to application/json (see
  // bulkImportPreview / blog upload-image for the same pattern).
  // Without this header axios sends a JSON request and Spring 415s.
  //
  // `backfill=true` (V139) flips on the create-posts-for-unmatched-
  // rows behaviour. Backend hard-errors when the connection isn't
  // linked to a brand via brand_default_connections — no first-
  // brand fallback. Personal-export rows are never backfilled
  // even when this is on (no post copy in TOP POSTS).
  importAnalytics: (
    workspaceId: string,
    connectionId: string,
    file: File,
    options?: { backfill?: boolean },
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    const qs = options?.backfill ? '?backfill=true' : '';
    return api
      .post<ImportJob>(
        `/api/workspaces/${workspaceId}/connections/by-id/${connectionId}/analytics/import${qs}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data);
  },
  getAnalyticsImportJob: (workspaceId: string, connectionId: string, jobId: string) =>
    api
      .get<ImportJob>(
        `/api/workspaces/${workspaceId}/connections/by-id/${connectionId}/analytics/import/jobs/${jobId}`,
      )
      .then((r) => r.data),
  listAnalyticsImportJobs: (workspaceId: string, connectionId: string) =>
    api
      .get<
        ImportJob[]
      >(`/api/workspaces/${workspaceId}/connections/by-id/${connectionId}/analytics/import/jobs`)
      .then((r) => r.data),
  disconnect: (workspaceId: string, platform: string) =>
    api.delete(`/api/workspaces/${workspaceId}/connections/${platform}`).then((r) => r.data),
  // Update display name
  updateDisplayName: (workspaceId: string, connectionId: string, displayName: string) =>
    api
      .patch<PlatformConnection>(
        `/api/workspaces/${workspaceId}/connections/by-id/${connectionId}`,
        { displayName },
      )
      .then((r) => r.data),
  // Disconnect by ID
  disconnectById: (workspaceId: string, connectionId: string) =>
    api
      .delete(`/api/workspaces/${workspaceId}/connections/by-id/${connectionId}`)
      .then((r) => r.data),
  // V85 — connection-scoped audit log (xyflow trace for sync/publish decisions).
  events: (workspaceId: string, connectionId: string) =>
    api
      .get<
        ConnectionEvent[]
      >(`/api/workspaces/${workspaceId}/connections/by-id/${connectionId}/events`)
      .then((r) => r.data),
  // Which brands (if any) use each workspace connection as their default posting account.
  // Returns one row per workspace connection; `brandIds` empty = unclaimed.
  brandClaims: (workspaceId: string) =>
    api
      .get<
        { connectionId: string; brandIds: string[] }[]
      >(`/api/workspaces/${workspaceId}/connections/brand-claims`)
      .then((r) => r.data),
  // Brand-level connections
  listBrand: (workspaceId: string, brandId: string) =>
    api
      .get<PlatformConnection[]>(`/api/workspaces/${workspaceId}/brands/${brandId}/connections`)
      .then((r) => r.data),
  disconnectBrand: (workspaceId: string, brandId: string, platform: string) =>
    api
      .delete(`/api/workspaces/${workspaceId}/brands/${brandId}/connections/${platform}`)
      .then((r) => r.data),
  getAuthUrlBrand: (workspaceId: string, brandId: string, platform: string, redirectUri: string) =>
    api
      .get<{
        url: string;
      }>(`/api/workspaces/${workspaceId}/brands/${brandId}/connections/${platform}/authorize`, {
        params: { redirectUri },
      })
      .then((r) => r.data),
  // Brand default connections (posting identity)
  getDefaults: (workspaceId: string, brandId: string) =>
    api
      .get<
        { platform: string; connectionId: string; displayName: string; accountType: string }[]
      >(`/api/workspaces/${workspaceId}/brands/${brandId}/default-connections`)
      .then((r) => r.data),
  setDefault: (workspaceId: string, brandId: string, platform: string, connectionId: string) =>
    api
      .put(`/api/workspaces/${workspaceId}/brands/${brandId}/default-connections`, {
        platform,
        connectionId,
      })
      .then((r) => r.data),
  // Update posting connection on a post
  updatePostingConnection: (workspaceId: string, postId: string, connectionId: string) =>
    api
      .patch(`/api/workspaces/${workspaceId}/posts/${postId}/posting-connection`, { connectionId })
      .then((r) => r.data),
};

/** Webhook health + management for the connections page (Phase 12.1). */
export interface WebhookConnectionStatus {
  connectionId: string;
  platform?: string;
  accountId: string | null;
  accountName: string | null;
  subscribed: boolean;
  fields?: string[];
  /** null when subscribed; otherwise: 'oauth1_required' | 'subscribe_pending'
   *  | 'not_subscribed' | 'missing_account_id' | 'webhook_url_not_registered'
   *  | 'subscribe_check_failed' */
  issue: string | null;
}

export interface MetaWebhookStatus {
  endpoint: string;
  connections: WebhookConnectionStatus[];
}

export interface XWebhookStatus {
  endpoint: string;
  webhookId: string | null;
  registered: boolean;
  valid: boolean;
  subscribedUserIds: string[];
  connections: WebhookConnectionStatus[];
  error?: string | null;
}

export const webhookApi = {
  metaStatus: (workspaceId: string) =>
    api
      .get<MetaWebhookStatus>('/api/webhooks/meta/status', { params: { workspaceId } })
      .then((r) => r.data),
  xStatus: (workspaceId: string) =>
    api
      .get<XWebhookStatus>('/api/webhooks/x/status', { params: { workspaceId } })
      .then((r) => r.data),
  /** Step 1 of the X OAuth 1.0a flow — returns the X authorize URL the
   *  frontend should redirect the user to. The user comes back via
   *  /oauth/twitter1/callback (handled by the backend, then bounced to
   *  /dashboard/settings/connections?x_webhook_enabled=<connectionId>). */
  startXOauth1: (connectionId: string) =>
    api
      .post<{
        authorizeUrl: string;
        requestToken: string;
      }>(`/api/connections/${connectionId}/x-webhook-oauth1`)
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Outbound webhooks (Phase A) + native integrations (Phase B)
// Connected Apps page — `/api/workspaces/:id/webhooks` +
// `/api/workspaces/:id/integrations`. OWNER-only. Custom webhooks
// gated by the `custom_webhooks` feature flag (Pro+); Slack/Discord
// available on every plan including Starter.
// ---------------------------------------------------------------------------

export type WebhookEventType =
  | 'post.published'
  | 'post.failed'
  | 'post.approved'
  | 'engagement.new_comment'
  | 'engagement.auto_replied'
  | 'cadence.generated'
  | 'report.ready'
  | 'connection.unhealthy';

export const ALL_WEBHOOK_EVENT_TYPES: WebhookEventType[] = [
  'post.published',
  'post.failed',
  'post.approved',
  'engagement.new_comment',
  'engagement.auto_replied',
  'cadence.generated',
  'report.ready',
  'connection.unhealthy',
];

export interface OutboundWebhookEndpoint {
  id: string;
  name: string;
  url: string;
  hasSecret: boolean;
  events: WebhookEventType[];
  enabled: boolean;
  lastTriggeredAt: string | null;
  lastStatusCode: number | null;
  failureCount: number;
  createdAt: string | null;
}

export interface CreateOutboundWebhookRequest {
  name: string;
  url: string;
  secret?: string;
  events: WebhookEventType[];
  enabled?: boolean;
}

export interface UpdateOutboundWebhookRequest {
  name?: string;
  url?: string;
  secret?: string;
  events?: WebhookEventType[];
  enabled?: boolean;
}

export interface TestWebhookResponse {
  statusCode: number | null;
  triggeredAt: string | null;
  failureCount: number;
  enabled: boolean;
}

export interface WebhookDelivery {
  id: string;
  eventType: WebhookEventType;
  statusCode: number | null;
  durationMs: number | null;
  deliveredAt: string;
  responseBody: string | null;
}

export const outboundWebhookApi = {
  list: (workspaceId: string) =>
    api
      .get<OutboundWebhookEndpoint[]>(`/api/workspaces/${workspaceId}/webhooks`)
      .then((r) => r.data),
  create: (workspaceId: string, body: CreateOutboundWebhookRequest) =>
    api
      .post<OutboundWebhookEndpoint>(`/api/workspaces/${workspaceId}/webhooks`, body)
      .then((r) => r.data),
  update: (workspaceId: string, webhookId: string, body: UpdateOutboundWebhookRequest) =>
    api
      .patch<OutboundWebhookEndpoint>(`/api/workspaces/${workspaceId}/webhooks/${webhookId}`, body)
      .then((r) => r.data),
  delete: (workspaceId: string, webhookId: string) =>
    api.delete<void>(`/api/workspaces/${workspaceId}/webhooks/${webhookId}`).then((r) => r.data),
  test: (workspaceId: string, webhookId: string) =>
    api
      .post<TestWebhookResponse>(`/api/workspaces/${workspaceId}/webhooks/${webhookId}/test`)
      .then((r) => r.data),
  deliveries: (workspaceId: string, webhookId: string, limit = 50) =>
    api
      .get<
        WebhookDelivery[]
      >(`/api/workspaces/${workspaceId}/webhooks/${webhookId}/deliveries`, { params: { limit } })
      .then((r) => r.data),
};

export type IntegrationType = 'slack' | 'discord';

export interface IntegrationConnection {
  id: string;
  type: IntegrationType;
  webhookUrlMasked: string;
  channelName: string | null;
  events: WebhookEventType[];
  enabled: boolean;
  createdAt: string | null;
}

export interface CreateIntegrationRequest {
  type: IntegrationType;
  webhookUrl: string;
  channelName?: string;
  events: WebhookEventType[];
  enabled?: boolean;
}

export interface UpdateIntegrationRequest {
  webhookUrl?: string;
  channelName?: string;
  events?: WebhookEventType[];
  enabled?: boolean;
}

export const integrationApi = {
  list: (workspaceId: string) =>
    api
      .get<IntegrationConnection[]>(`/api/workspaces/${workspaceId}/integrations`)
      .then((r) => r.data),
  create: (workspaceId: string, body: CreateIntegrationRequest) =>
    api
      .post<IntegrationConnection>(`/api/workspaces/${workspaceId}/integrations`, body)
      .then((r) => r.data),
  update: (workspaceId: string, integrationId: string, body: UpdateIntegrationRequest) =>
    api
      .patch<IntegrationConnection>(
        `/api/workspaces/${workspaceId}/integrations/${integrationId}`,
        body,
      )
      .then((r) => r.data),
  delete: (workspaceId: string, integrationId: string) =>
    api
      .delete<void>(`/api/workspaces/${workspaceId}/integrations/${integrationId}`)
      .then((r) => r.data),
  test: (workspaceId: string, integrationId: string) =>
    api
      .post<{
        triggeredAt: string;
      }>(`/api/workspaces/${workspaceId}/integrations/${integrationId}/test`)
      .then((r) => r.data),
};
